import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';

import { AuthShell } from '@/components/auth/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { authService } from '@/services/authService';

const forgotSchema = z.object({
  email: z.string().min(1, { message: 'common.required' }).email({ message: 'auth.email_invalid' }),
});

type ForgotFormValues = z.infer<typeof forgotSchema>;

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const form = useForm<ForgotFormValues>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: '' },
  });

  async function onSubmit(values: ForgotFormValues) {
    try {
      await authService.requestPasswordReset(values.email);
    } catch {
      // Endpoint is silent-on-miss (no enumeration); always surface success.
    }
    setSubmittedEmail(values.email);
  }

  if (submittedEmail) {
    return (
      <AuthShell title={t('auth.forgot_title')}>
        <div className="space-y-6">
          <div className="rounded-lg border border-border p-6 text-center space-y-3">
            <Mail className="h-8 w-8 mx-auto text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">{t('auth.forgot_success')}</p>
          </div>
          <p className="text-center text-sm">
            <Link
              to="/login"
              className="text-foreground font-medium underline-offset-4 hover:underline"
            >
              {t('common.back')}
            </Link>
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t('auth.forgot_title')}
      subtitle={t('auth.forgot_subtitle')}
      footer={
        <Link
          to="/login"
          className="text-foreground font-medium underline-offset-4 hover:underline"
        >
          {t('common.back')}
        </Link>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.email')}</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="email" autoFocus className="h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full h-11" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? t('common.loading') : t('auth.forgot_button')}
          </Button>
        </form>
      </Form>
    </AuthShell>
  );
}
